import React, { useEffect, useState } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import AuthScreen from './components/AuthScreen';
import AnalyzerApp from './components/AnalyzerApp';
import PricingPage from './pages/PricingPage';
import BillingSuccessPage from './pages/BillingSuccessPage';
import JournalPage from './pages/JournalPage';
import PerformancePage from './pages/PerformancePage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import RiskDisclaimerPage from './pages/RiskDisclaimerPage';
import BillingTermsPage from './pages/BillingTermsPage';
import OnboardingPage from './pages/OnboardingPage';
import LegalFooter from './components/LegalFooter';
import ScrollToTop from './components/ScrollToTop';
import FeedbackPage from './pages/FeedbackPage';
import AdminFeedbackPage from './pages/AdminFeedbackPage';
import AnalyticsPageTracker from './components/AnalyticsPageTracker';
import { trackProductEventOncePerSession } from './lib/productEvents';

function AppRoutes({ session, theme, setTheme }) {
  return (
    <>
      <Routes>
        <Route
          path="/"
          element={
            <AnalyzerApp
              session={session}
              theme={theme}
              setTheme={setTheme}
            />
          }
        />
        <Route
          path="/journal"
          element={
            <JournalPage
              session={session}
              theme={theme}
              setTheme={setTheme}
            />
          }
        />
        <Route
          path="/performance"
          element={
            <PerformancePage
              session={session}
              theme={theme}
              setTheme={setTheme}
            />
          }
        />
        <Route
          path="/pricing"
          element={<PricingPage session={session} theme={theme} />}
        />
        <Route
          path="/billing/success"
          element={<BillingSuccessPage session={session} theme={theme} />}
        />
        <Route
          path="/onboarding"
          element={<OnboardingPage theme={theme} />}
        />
        <Route
          path="/feedback"
          element={<FeedbackPage session={session} theme={theme} />}
        />
        <Route
          path="/admin-feedback"
          element={<AdminFeedbackPage session={session} theme={theme} />}
        />
        <Route
          path="/terms"
          element={<TermsPage theme={theme} />}
        />
        <Route
          path="/privacy"
          element={<PrivacyPage theme={theme} />}
        />
        <Route
          path="/risk-disclaimer"
          element={<RiskDisclaimerPage theme={theme} />}
        />
        <Route
          path="/billing-terms"
          element={<BillingTermsPage theme={theme} />}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <LegalFooter />
    </>
  );
}

function App() {
  const [session, setSession] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [passwordRecovery, setPasswordRecovery] = useState(false);

  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('stockvested-theme');
    return savedTheme || 'dark';
  });

  useEffect(() => {
    localStorage.setItem('stockvested-theme', theme);
    document.body.setAttribute('data-theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    const loadSession = async () => {
      const { data, error } = await supabase.auth.getSession();

      if (error) {
        console.error('Session load error:', error.message);
      }

      setSession(data.session ?? null);
      setSessionLoading(false);
    };

    loadSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (event === 'PASSWORD_RECOVERY') {
        setPasswordRecovery(true);
      }

      setSession(nextSession ?? null);
      setSessionLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const sessionUserId = session?.user?.id;
  const sessionUserEmail = session?.user?.email;

  useEffect(() => {
    if (!sessionUserId) return;

    let cancelled = false;

    async function recordAuthenticatedSession() {
      const {error: profileError} = await supabase
        .from('profiles')
        .upsert(
          {
            id: sessionUserId,
            email: sessionUserEmail,
          },
          {
            onConflict: 'id',
            ignoreDuplicates: true,
          }
        );

      if (profileError) {
        console.error(
          'Unable to ensure profile before session event:',
          profileError
        );
        return;
      }

      if (cancelled) return;

      await trackProductEventOncePerSession({
        userId: sessionUserId,
        eventName: 'app_session_started',
        dedupeKey: 'app_session_started',
        metadata: {
          initial_path: window.location.pathname,
        },
      });
    }

    recordAuthenticatedSession();

    return () => {
      cancelled = true;
    };
  }, [sessionUserId, sessionUserEmail]);

  const handleRecoveryComplete = async () => {
    setPasswordRecovery(false);

    const { data, error } = await supabase.auth.getSession();

    if (error) {
      console.error('Session reload error:', error.message);
    }

    setSession(data.session ?? null);
  };

  if (sessionLoading) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <h1 className="auth-title">Loading workspace...</h1>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <ScrollToTop />
      <AnalyticsPageTracker />

      {passwordRecovery ? (
        <AuthScreen
          recoveryMode
          onRecoveryComplete={handleRecoveryComplete}
        />
      ) : !session ? (
        <AuthScreen />
      ) : (
        <AppRoutes
          session={session}
          theme={theme}
          setTheme={setTheme}
        />
      )}
    </BrowserRouter>
  );
}

export default App;